import { cn } from '@/lib/utils';
import { formatCurrency, formatPercentage } from '@/lib/foco-calculations';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface QuickStat {
  label: string;
  current: number;
  previous?: number;
  format: 'currency' | 'percentage' | 'number';
  currency?: string;
}

interface QuickStatsBarProps {
  stats: QuickStat[];
  className?: string;
}

export function QuickStatsBar({ stats, className }: QuickStatsBarProps) {
  const formatValue = (value: number, format: string, currency?: string) => {
    switch (format) {
      case 'currency':
        return formatCurrency(value, currency || 'CRC');
      case 'percentage':
        return formatPercentage(value);
      default:
        return value.toLocaleString();
    }
  };

  const getChangeIndicator = (current: number, previous?: number) => {
    if (previous === undefined || previous === 0) return null;
    
    const change = ((current - previous) / previous) * 100;
    
    if (Math.abs(change) < 1) {
      return { icon: Minus, color: 'text-muted-foreground', value: '0%' };
    }
    
    if (change > 0) {
      return { 
        icon: TrendingUp, 
        color: 'text-success', 
        value: `+${change.toFixed(0)}%` 
      };
    }
    
    return { 
      icon: TrendingDown, 
      color: 'text-destructive', 
      value: `${change.toFixed(0)}%` 
    };
  };

  return (
    <div className={cn(
      'flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1',
      className
    )}>
      {stats.map((stat, index) => {
        const change = getChangeIndicator(stat.current, stat.previous);
        
        return (
          <div 
            key={stat.label}
            className={cn(
              'quick-stat flex-shrink-0 min-w-[140px] opacity-0 animate-slide-up',
            )}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide truncate">
                {stat.label}
              </p>
              <p className="text-sm font-bold truncate">
                {formatValue(stat.current, stat.format, stat.currency)}
              </p>
            </div>
            {change && (
              <div className={cn('flex items-center gap-0.5', change.color)}>
                <change.icon className="h-3 w-3" />
                <span className="text-[10px] font-medium">{change.value}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
